import React, { useState, useEffect } from 'react';
import { Table, Tag, Button, Modal, Form, Input, Select, message, Row, Col } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import api from '../api/axios';
import { DEVICE_TYPES, deviceTypeOptions, isClassBoard } from '../utils/deviceTypes';

const { Option } = Select;

const DeviceList = ({ deviceType = DEVICE_TYPES.CLASS_BOARD, onDeviceSelect }) => {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editingDevice, setEditingDevice] = useState(null);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [schools, setSchools] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [selectedClass, setSelectedClass] = useState(null);
  const currentIsClassBoard = isClassBoard(deviceType);

  // 获取设备列表
  const fetchDevices = async () => {
    setLoading(true);
    try {
      const params = { deviceType };
      if (selectedSchool) params.schoolId = selectedSchool;
      if (currentIsClassBoard && selectedClass) params.classId = selectedClass;
      
      const response = await api.get('/admin/devices', { params });
      setDevices(response.data);
    } catch (error) {
      message.error('获取设备列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 获取学校列表
  const fetchSchools = async () => {
    try {
      const response = await api.get('/admin/devices/schools');
      setSchools(response.data);
    } catch (error) {
      message.error('获取学校列表失败');
    }
  };

  // 根据学校ID获取班级列表
  const fetchClasses = async (schoolId) => {
    if (!schoolId) {
      setClasses([]);
      setSelectedClass(null);
      return;
    }
    try {
      const response = await api.get(`/admin/devices/schools/${schoolId}/classes`);
      setClasses(response.data);
      setSelectedClass(null);
    } catch (error) {
      message.error('获取班级列表失败');
    }
  };

  useEffect(() => {
    fetchDevices();
  }, [selectedSchool, selectedClass, deviceType]);

  useEffect(() => {
    setSelectedSchool(null);
    setSelectedClass(null);
    setClasses([]);
  }, [deviceType]);

  useEffect(() => {
    fetchSchools();
  }, []);

  // 添加设备
  const handleAddDevice = async (values) => {
    try {
      // 转换字段名格式
      const deviceData = {
        deviceId: values.deviceId,
        name: values.name,
        type: deviceType,
        schoolId: values.schoolId,
        classId: currentIsClassBoard ? values.classId : null
      };
      await api.post('/admin/devices', deviceData);
      message.success('设备添加成功');
      setIsModalVisible(false);
      form.resetFields();
      fetchDevices();
    } catch (error) {
      message.error('设备添加失败');
    }
  };

  // 删除设备
  const handleDeleteDevice = async (id) => {
    try {
      await api.delete(`/admin/devices/${id}`);
      message.success('设备删除成功');
      fetchDevices();
    } catch (error) {
      message.error('设备删除失败');
    }
  };

  // 打开编辑弹窗
  const handleEditDevice = (record) => {
    setEditingDevice(record);
    // 设置表单初始值
    editForm.setFieldsValue({
      name: record.name,
      type: deviceType,
      schoolId: record.school_id,
      classId: currentIsClassBoard ? record.class_id : null
    });
    // 如果有学校ID，先获取该学校的班级列表
    if (record.school_id) {
      fetchClassesForEdit(record.school_id);
    }
    setIsEditModalVisible(true);
  };

  // 根据学校ID获取班级列表（用于编辑弹窗）
  const fetchClassesForEdit = async (schoolId) => {
    try {
      const response = await api.get(`/admin/devices/schools/${schoolId}/classes`);
      setClasses(response.data);
    } catch (error) {
      message.error('获取班级列表失败');
    }
  };

  // 提交编辑
  const handleSubmitEdit = async (values) => {
    try {
      const updateData = {
        name: values.name,
        type: deviceType,
        schoolId: values.schoolId,
        classId: currentIsClassBoard ? values.classId : null
      };
      await api.put(`/admin/devices/${editingDevice.id}`, updateData);
      message.success('设备更新成功');
      setIsEditModalVisible(false);
      setEditingDevice(null);
      editForm.resetFields();
      fetchDevices();
    } catch (error) {
      message.error('设备更新失败');
    }
  };

  // 状态标签
  const getStatusTag = (status) => {
    return status === '在线' ? (
      <Tag color="green">在线</Tag>
    ) : (
      <Tag color="red">离线</Tag>
    );
  };

  // 电池状态
  const getBatteryStatus = (battery) => {
    if (battery > 70) {
      return <Tag color="green">{battery}%</Tag>;
    } else if (battery > 30) {
      return <Tag color="orange">{battery}%</Tag>;
    } else {
      return <Tag color="red">{battery}%</Tag>;
    }
  };

  const columns = [
    {
      title: '设备名称',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '设备ID',
      dataIndex: 'device_id',
      key: 'device_id',
    },
    {
      title: '设备类型',
      dataIndex: 'type',
      key: 'type',
    },
    {
        title: '学校',
        dataIndex: 'school_name',
        key: 'school_name',
      },
      {
        title: '班级',
        dataIndex: 'class_name',
        key: 'class_name',
      },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => getStatusTag(status),
    },
    {
      title: '电池电量',
      dataIndex: 'battery',
      key: 'battery',
      render: (battery) => getBatteryStatus(battery),
    },
    {
      title: 'IP地址',
      dataIndex: 'ip_address',
      key: 'ip_address',
    },
    {
      title: '软件版本',
      dataIndex: 'version',
      key: 'version',
      render: (version) => version || '-',
    },
    {
      title: '最后心跳',
      dataIndex: 'last_heartbeat',
      key: 'last_heartbeat',
      render: (timestamp) => new Date(timestamp).toLocaleString(),
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <div>
          <Button type="primary" style={{ marginRight: 8 }} onClick={() => onDeviceSelect(record)}>
            查看详情
          </Button>
          <Button type="default" style={{ marginRight: 8 }} onClick={() => handleEditDevice(record)}>
            编辑
          </Button>
          <Button danger onClick={() => handleDeleteDevice(record.id)}>
            删除
          </Button>
        </div>
      ),
    },
  ];

  const visibleColumns = currentIsClassBoard
    ? columns
    : columns.filter(column => column.key !== 'class_name');

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2>{deviceType}设备列表</h2>
        <Button
          type="primary"
          onClick={() => {
            form.setFieldsValue({ type: deviceType, classId: null });
            setIsModalVisible(true);
          }}
        >
          添加设备
        </Button>
      </div>
      
      <div style={{ marginBottom: 16, padding: 16, backgroundColor: '#f5f5f5', borderRadius: 4 }}>
        <h3>查询条件</h3>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item label="学校" style={{ marginBottom: 0 }}>
              <Select
                placeholder="请选择学校"
                style={{ width: '100%' }}
                value={selectedSchool}
                onChange={(value) => {
                  setSelectedSchool(value);
                  fetchClasses(value);
                }}
                showSearch
                filterOption={(input, option) => {
                  return (option.children || '').toLowerCase().includes(input.toLowerCase());
                }}
              >
                {schools.map((school) => (
                  <Option key={school.ID} value={school.ID}>
                    {school.FULL_NAME || school.ABBREVIATION}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          {currentIsClassBoard && (
            <Col span={8}>
              <Form.Item label="班级" style={{ marginBottom: 0 }}>
                <Select
                  placeholder="请选择班级"
                  style={{ width: '100%' }}
                  value={selectedClass}
                  onChange={setSelectedClass}
                  disabled={!selectedSchool}
                  showSearch
                  filterOption={(input, option) => {
                    return (option.children || '').toLowerCase().includes(input.toLowerCase());
                  }}
                >
                  {classes.map((cls) => (
                    <Option key={cls.ID} value={cls.ID}>
                      {cls.NAME}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          )}
          <Col span={8}>
            <Button 
              type="primary" 
              icon={<SearchOutlined />} 
              style={{ marginTop: 24 }}
              onClick={fetchDevices}
            >
              查询
            </Button>
            <Button 
              style={{ marginLeft: 8, marginTop: 24 }}
              onClick={() => {
                setSelectedSchool(null);
                setSelectedClass(null);
                setClasses([]);
              }}
            >
              重置
            </Button>
          </Col>
        </Row>
      </div>
      
      <Table 
        columns={visibleColumns}
        dataSource={devices} 
        rowKey="id" 
        loading={loading}
        pagination={{ pageSize: 10 }}
      />

      <Modal
        title="添加设备"
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleAddDevice}
        >
          <Form.Item
            name="deviceId"
            label="设备ID"
            rules={[{ required: true, message: '请输入设备ID' }]}
          >
            <Input placeholder="请输入设备ID" />
          </Form.Item>
          <Form.Item
            name="name"
            label="设备名称"
            rules={[{ required: true, message: '请输入设备名称' }]}
          >
            <Input placeholder="请输入设备名称" />
          </Form.Item>
          <Form.Item
            name="type"
            label="设备类型"
            rules={[{ required: true, message: '请选择设备类型' }]}
          >
            <Select disabled options={deviceTypeOptions} />
          </Form.Item>
          <Form.Item
            name="schoolId"
            label="学校"
            rules={[{ required: true, message: '请选择学校' }]}
          >
            <Select
              placeholder="请选择学校"
              style={{ width: '100%' }}
              showSearch
              filterOption={(input, option) => {
                return (option.children || '').toLowerCase().includes(input.toLowerCase());
              }}
              onChange={(value) => {
                form.setFieldsValue({ classId: null });
                fetchClasses(value);
              }}
            >
              {schools.map((school) => (
                <Option key={school.ID} value={school.ID}>
                  {school.FULL_NAME || school.ABBREVIATION}
                </Option>
              ))}
            </Select>
          </Form.Item>
          {currentIsClassBoard && (
            <Form.Item
              name="classId"
              label="班级"
              rules={[{ required: true, message: '请选择班级' }]}
            >
              <Select
                placeholder="请选择班级"
                style={{ width: '100%' }}
                disabled={!form.getFieldValue('schoolId')}
                showSearch
                filterOption={(input, option) => {
                  return (option.children || '').toLowerCase().includes(input.toLowerCase());
                }}
              >
                {classes.map((cls) => (
                  <Option key={cls.ID} value={cls.ID}>
                    {cls.NAME}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}
          <Form.Item>
            <Button type="primary" htmlType="submit" style={{ marginRight: 8 }}>
              确认
            </Button>
            <Button onClick={() => setIsModalVisible(false)}>
              取消
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* 编辑设备弹窗 */}
      <Modal
        title="编辑设备"
        open={isEditModalVisible}
        onCancel={() => {
          setIsEditModalVisible(false);
          setEditingDevice(null);
          editForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleSubmitEdit}
        >
          <Form.Item
            name="name"
            label="设备名称"
            rules={[{ required: true, message: '请输入设备名称' }]}
          >
            <Input placeholder="请输入设备名称" />
          </Form.Item>
          <Form.Item
            name="type"
            label="设备类型"
            rules={[{ required: true, message: '请选择设备类型' }]}
          >
            <Select disabled options={deviceTypeOptions} />
          </Form.Item>
          <Form.Item
            name="schoolId"
            label="学校"
            rules={[{ required: true, message: '请选择学校' }]}
          >
            <Select
              placeholder="请选择学校"
              style={{ width: '100%' }}
              showSearch
              filterOption={(input, option) => {
                return (option.children || '').toLowerCase().includes(input.toLowerCase());
              }}
              onChange={(value) => {
                editForm.setFieldsValue({ classId: null });
                fetchClassesForEdit(value);
              }}
            >
              {schools.map((school) => (
                <Option key={school.ID} value={school.ID}>
                  {school.FULL_NAME || school.ABBREVIATION}
                </Option>
              ))}
            </Select>
          </Form.Item>
          {currentIsClassBoard && (
            <Form.Item
              name="classId"
              label="班级"
              rules={[{ required: true, message: '请选择班级' }]}
            >
              <Select
                placeholder="请选择班级"
                style={{ width: '100%' }}
                showSearch
                filterOption={(input, option) => {
                  return (option.children || '').toLowerCase().includes(input.toLowerCase());
                }}
              >
                {classes.map((cls) => (
                  <Option key={cls.ID} value={cls.ID}>
                    {cls.NAME}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}
          <Form.Item>
            <Button type="primary" htmlType="submit" style={{ marginRight: 8 }}>
              确认
            </Button>
            <Button onClick={() => {
              setIsEditModalVisible(false);
              setEditingDevice(null);
              editForm.resetFields();
            }}>
              取消
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default DeviceList;
